/**
 * Helpers puros usados por `ResumenEconomicoDocument.tsx`:
 *   - `numeroALetras(n)` — convierte 213118.50 → "Doscientos trece mil
 *     ciento dieciocho pesos 50/100 M.N." (estándar contable MX).
 *   - `buildCalendarioPagos(...)` — calendario híbrido: con fechas si hay
 *     `fecha_firma_est`, "Mes N" si no.
 *
 * Ambos puros, testeables sin React. Los tests viven en
 * `resumenPdfHelpers.test.ts`.
 */

/* ─────────────────────────────────────────────────────────────────────
 * numeroALetras (es-MX) — estándar contable
 * Cubre 0 a 999,999,999.99 — suficiente para contratos agrarios
 * (típicos $1-50M MXN).
 * ───────────────────────────────────────────────────────────────────── */

const UNIDADES = [
  '', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
  'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete',
  'dieciocho', 'diecinueve', 'veinte', 'veintiuno', 'veintidós', 'veintitrés',
  'veinticuatro', 'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho',
  'veintinueve',
];
const DECENAS = [
  '', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta',
  'ochenta', 'noventa',
];
const CENTENAS = [
  '', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos',
  'seiscientos', 'setecientos', 'ochocientos', 'novecientos',
];

/** 0..999 → palabras (sin "y" leading, sin "pesos"). Ej: 234 → "doscientos treinta y cuatro". */
function tresDigitos(n: number): string {
  if (n === 0) return '';
  if (n === 100) return 'cien';

  const c = Math.floor(n / 100);
  const r = n % 100;

  let parte = '';
  if (c > 0) parte = CENTENAS[c]!;

  if (r > 0) {
    if (parte) parte += ' ';
    if (r < 30) {
      parte += UNIDADES[r];
    } else {
      const d = Math.floor(r / 10);
      const u = r % 10;
      parte += DECENAS[d]!;
      if (u > 0) parte += ` y ${UNIDADES[u]}`;
    }
  }

  return parte;
}

/** Entero >= 0 a palabras (sin "pesos"). Maneja millones, miles, centenas. */
function enteroALetras(n: number): string {
  if (n === 0) return 'cero';

  const millones = Math.floor(n / 1_000_000);
  const miles = Math.floor((n % 1_000_000) / 1_000);
  const resto = n % 1_000;

  const partes: string[] = [];

  if (millones > 0) {
    if (millones === 1) partes.push('un millón');
    else partes.push(`${tresDigitos(millones)} millones`);
  }

  if (miles > 0) {
    if (miles === 1) partes.push('mil');
    else partes.push(`${tresDigitos(miles)} mil`);
  }

  if (resto > 0) {
    partes.push(tresDigitos(resto));
  }

  return partes.join(' ').trim();
}

/**
 * Convierte un número decimal a palabras en formato contable MX:
 *   213118.50 → "Doscientos trece mil ciento dieciocho pesos 50/100 M.N."
 *
 * Reglas:
 *   - Primera letra capitalizada.
 *   - Pluraliza correctamente: "1 peso" vs "0 pesos" vs "2 pesos".
 *   - Centavos siempre 2 dígitos con padding ("8 centavos" → "08/100").
 *   - "M.N." al final = Moneda Nacional (estándar de cheques en México).
 *   - Devuelve "" para null/NaN/negativos (caller maneja el "—").
 */
export function numeroALetras(monto: number | null | undefined): string {
  if (monto == null || !Number.isFinite(monto) || monto < 0) return '';

  // Redondeo a 2 decimales para estabilidad (5.005 → 5.01).
  const redondeado = Math.round(monto * 100) / 100;
  const entero = Math.floor(redondeado);
  const centavos = Math.round((redondeado - entero) * 100);

  // Edge case: 0
  if (entero === 0 && centavos === 0) {
    return 'Cero pesos 00/100 M.N.';
  }

  let palabras = enteroALetras(entero);
  // Capitaliza primera letra
  palabras = palabras.charAt(0).toUpperCase() + palabras.slice(1);

  const pesoLabel = entero === 1 ? 'peso' : 'pesos';
  const cents = String(centavos).padStart(2, '0');

  return `${palabras} ${pesoLabel} ${cents}/100 M.N.`;
}

/* ─────────────────────────────────────────────────────────────────────
 * Calendario de pagos
 * ───────────────────────────────────────────────────────────────────── */

export interface PagoMes {
  /** 1-indexed: primer pago = mes 1. */
  num: number;
  /** ISO YYYY-MM-DD si hay fecha_firma_est; null si no. */
  fechaIso: string | null;
  /** Etiqueta legible para el PDF. "15 jul 2026" o "Mes 1". */
  fechaLabel: string;
  monto: number;
  acumulado: number;
}

/**
 * Construye el calendario de pagos diferidos.
 *
 * Reglas:
 *   - Sin fecha_firma_est → fechaLabel = "Mes 1", "Mes 2", ...
 *   - Con fecha_firma_est → fechaLabel = primer pago a 1 mes después de firma,
 *     siguientes mensuales. Mismo día del mes que la firma; si el mes destino
 *     no tiene ese día (ej. firma 31-ene → 28-feb), cae al último día del mes.
 *   - El monto es exactamente subtotal/meses; los redondeos se acumulan en
 *     el último pago para que sum(montos) === subtotal exacto.
 *
 * Devuelve array vacío si meses=0 o subtotal=0 (no hay nada que distribuir).
 */
export function buildCalendarioPagos(args: {
  subtotalConstruccion: number | null | undefined;
  meses: number | null | undefined;
  fechaFirmaIso: string | null | undefined;
}): PagoMes[] {
  const { subtotalConstruccion, meses, fechaFirmaIso } = args;
  if (
    subtotalConstruccion == null ||
    !Number.isFinite(subtotalConstruccion) ||
    subtotalConstruccion <= 0
  ) {
    return [];
  }
  if (meses == null || !Number.isFinite(meses) || meses <= 0) return [];

  const mesesInt = Math.trunc(meses);

  // Monto base con redondeo a centavo. La diferencia residual va al último
  // pago para que la suma == subtotal exacto (libre de drift).
  const montoBaseRaw = subtotalConstruccion / mesesInt;
  const montoBase = Math.round(montoBaseRaw * 100) / 100;
  const sumaSinUltimo = montoBase * (mesesInt - 1);
  const montoUltimo = Math.round((subtotalConstruccion - sumaSinUltimo) * 100) / 100;

  // Parse fecha de firma. Anclamos al mediodía UTC para evitar shift de
  // timezone en es-MX (UTC-6).
  let fechaFirma: Date | null = null;
  if (fechaFirmaIso) {
    const d = new Date(`${fechaFirmaIso}T12:00:00Z`);
    if (!Number.isNaN(d.getTime())) fechaFirma = d;
  }

  const pagos: PagoMes[] = [];
  let acumulado = 0;
  for (let i = 1; i <= mesesInt; i++) {
    const monto = i === mesesInt ? montoUltimo : montoBase;
    acumulado = Math.round((acumulado + monto) * 100) / 100;

    let fechaIso: string | null = null;
    let fechaLabel: string;

    if (fechaFirma) {
      // Suma i meses al mes de firma, mismo día. Date#setMonth maneja el
      // overflow correctamente (31-ene + 1 mes → 02-mar, no 28-feb). Para
      // evitar ese drift, capeamos al último día del mes destino cuando el
      // día origen excede.
      const baseY = fechaFirma.getUTCFullYear();
      const baseM = fechaFirma.getUTCMonth();
      const baseD = fechaFirma.getUTCDate();
      const targetY = baseY + Math.floor((baseM + i) / 12);
      const targetMRaw = (baseM + i) % 12;
      const ultimoDia = new Date(Date.UTC(targetY, targetMRaw + 1, 0)).getUTCDate();
      const targetD = Math.min(baseD, ultimoDia);
      const dt = new Date(Date.UTC(targetY, targetMRaw, targetD, 12));
      const yyyy = dt.getUTCFullYear();
      const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(dt.getUTCDate()).padStart(2, '0');
      fechaIso = `${yyyy}-${mm}-${dd}`;
      fechaLabel = dt.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } else {
      fechaLabel = `Mes ${i}`;
    }

    pagos.push({ num: i, fechaIso, fechaLabel, monto, acumulado });
  }
  return pagos;
}

/* ─────────────────────────────────────────────────────────────────────
 * Folio del documento (único por export)
 * ───────────────────────────────────────────────────────────────────── */

/**
 * Genera el folio interno del documento — formato `RE-NNN-AAAAMMDD-HHMMSS-XXXX`:
 *   RE          = Resumen Económico
 *   NNN         = num_parcela zero-padded a 3 dígitos ("103" → "103",
 *                 "42A" → "42A", null → "000")
 *   AAAAMMDD    = fecha de generación (UTC), 8 dígitos
 *   HHMMSS      = hora de generación (UTC), 6 dígitos
 *   XXXX        = 4 chars hex aleatorios (16 bits de entropía)
 *
 * **Único por export**: timestamp UTC al segundo + 4 chars random ⇒ probabilidad
 * de colisión ≈ 1/65k incluso para dos exports en el mismo segundo de la misma
 * parcela. Reemplaza el folio determinista anterior (`RE-AAAA-NNN-XXXX`) que
 * generaba el MISMO código al re-emitir el documento.
 *
 * El `random` es inyectable para tests (4 chars hex uppercase). En prod usa
 * `crypto.getRandomValues` (disponible en navegador, Node ≥19, y CF Workers).
 *
 * Uso: cabecera del PDF (esquina superior derecha, look "ficha bancaria"
 * con número de control) + footer de cada página.
 */
export function buildFolioResumen(args: {
  parcelaId?: string | null;
  numParcela?: string | number | null;
  fecha?: Date;
  /** Override para tests; default = 4 chars hex random vía crypto. */
  random?: string;
}): string {
  const { numParcela, fecha = new Date(), random } = args;

  // NNN: si num_parcela es número, padding 3-dígitos. Si es string (sufijo
  // letra "42A"), uppercase + padding manual hasta 3 chars de fondo.
  let nnn: string;
  if (numParcela == null) {
    nnn = '000';
  } else if (typeof numParcela === 'number') {
    nnn = String(Math.trunc(numParcela)).padStart(3, '0');
  } else {
    const s = String(numParcela).trim().toUpperCase().slice(0, 4);
    nnn = s.padStart(3, '0');
  }

  // AAAAMMDD-HHMMSS en UTC
  const yyyy = fecha.getUTCFullYear();
  const mm = String(fecha.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(fecha.getUTCDate()).padStart(2, '0');
  const HH = String(fecha.getUTCHours()).padStart(2, '0');
  const MM = String(fecha.getUTCMinutes()).padStart(2, '0');
  const SS = String(fecha.getUTCSeconds()).padStart(2, '0');
  const stamp = `${yyyy}${mm}${dd}-${HH}${MM}${SS}`;

  // XXXX: 4 chars hex random uppercase. Inyectable para tests.
  const rand4 = (random ?? randomHex4()).toUpperCase().slice(0, 4).padEnd(4, 'X');

  return `RE-${nnn}-${stamp}-${rand4}`;
}

function randomHex4(): string {
  const bytes = new Uint8Array(2);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
